import { useQuery } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import { apiFetchJson } from '../../lib/apiFetch.js';
import type { SiteSuggestionsResponse } from './contractsMirror.js';
import { noteGeolocationDenied } from './LocationOffNotice.js';

/**
 * Job-site address lookup for the clock card, plus the browser position that biases it.
 *
 * WHY THE POSITION MATTERS: Geoapify without a proximity bias searches the whole country.
 * Measured on the api worker 2026-07-25 — "Pinewood Dr" unbiased returned Wilson / Winterville
 * / Ayden, 200+ miles east of the crew; biased, it returned Drexel / Morganton / Lincoln County.
 * The phone app grabs a fix before every lookup. The browser has to ask, and may be refused, so
 * a denied or unavailable position degrades the RESULTS and never blocks the punch.
 */

/** The api worker ignores anything shorter; mirrored here so we do not spend a request. */
export const MIN_QUERY_LENGTH = 3;

/** A slow or hanging position fix must not stall the address list behind it. */
const GEOLOCATION_TIMEOUT_MS = 8000;

/** A fix from a minute ago is still the right town. */
const GEOLOCATION_MAX_AGE_MS = 60_000;

export type PositionStatus = 'idle' | 'pending' | 'granted' | 'denied' | 'unavailable';

export interface BrowserPosition {
  lat: number;
  lng: number;
}

/**
 * Resolves the browser position ON DEMAND — never on mount, never on focus.
 *
 * The permission prompt is sticky: a user who hits Block is remembered and most never find the
 * setting again. So we spend the one ask at the only moment it is justified, when someone has
 * actually started typing an address. Picking an assigned job never triggers it.
 */
export function useBrowserPosition() {
  const [status, setStatus] = useState<PositionStatus>('idle');
  const [position, setPosition] = useState<BrowserPosition | null>(null);
  // Survives re-renders so a second keystroke cannot fire a second prompt.
  const inFlight = useRef(false);

  const request = useCallback(() => {
    if (inFlight.current || position) return;
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setStatus('unavailable');
      return;
    }
    inFlight.current = true;
    setStatus('pending');
    navigator.geolocation.getCurrentPosition(
      (fix) => {
        inFlight.current = false;
        setPosition({ lat: fix.coords.latitude, lng: fix.coords.longitude });
        noteGeolocationDenied(false);
        setStatus('granted');
      },
      (error) => {
        inFlight.current = false;
        // PERMISSION_DENIED is 1. Everything else (position unavailable, timeout, and the
        // Permissions-Policy block) is a soft failure the user may be able to retry.
        setStatus(error.code === 1 ? 'denied' : 'unavailable');
        noteGeolocationDenied(error.code === 1);
      },
      {
        enableHighAccuracy: false,
        timeout: GEOLOCATION_TIMEOUT_MS,
        maximumAge: GEOLOCATION_MAX_AGE_MS,
      },
    );
  }, [position]);

  // No separate retry: the error callback already clears `inFlight`, so calling `request`
  // again re-asks. A browser holding a persisted Block errors immediately without prompting,
  // which is the honest outcome — we surface it rather than pretending the ask is pending.
  return { status, position, request };
}

/**
 * Address suggestions for a settled query.
 *
 * The endpoint is gated on `time.clock`, ALWAYS answers 200, and fails soft to `[]` on a geocode
 * error, a timeout, a missing key or bad coords — so an empty list here is never distinguishable
 * from "no matches", and neither one is an error worth showing.
 */
export function useSiteSuggestionsQuery(query: string, position: BrowserPosition | null) {
  const trimmed = query.trim();
  const enabled = trimmed.length >= MIN_QUERY_LENGTH;

  return useQuery<SiteSuggestionsResponse>({
    // The position is part of the key: the same text biased from a different place is a
    // different question, and the answer must not come back from the unbiased cache entry.
    queryKey: [
      'timesheets',
      'site-suggestions',
      trimmed,
      position?.lat ?? null,
      position?.lng ?? null,
    ],
    enabled,
    // Addresses do not move. Re-asking Geoapify for the same string costs money and time.
    staleTime: 5 * 60_000,
    queryFn: () => {
      const params = new URLSearchParams({ q: trimmed });
      if (position) {
        params.set('lat', String(position.lat));
        params.set('lng', String(position.lng));
      }
      return apiFetchJson<SiteSuggestionsResponse>(
        `/api/v1/timesheets/site-suggestions?${params.toString()}`,
      );
    },
  });
}
